/**
 * Fire-and-forget client telemetry. Drops silently on error so failures
 * never affect the UX. Anonymous visitors get a session id stored locally;
 * authenticated requests send the JWT so the server can attribute the event.
 */

const apiUrl =
  process.env.REACT_APP_ENV === "LOCAL"
    ? "http://localhost:5000"
    : "https://api.spotprisprognos.se:8443";

const SESSION_KEY = "sp_session_id";

function getOrCreateSessionId(): string {
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = (crypto.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now().toString(36)).slice(0, 64);
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return "no-storage";
  }
}

function readCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : undefined;
}

function readAuthHeader(): string | undefined {
  const token = readCookie("_auth");
  const type = readCookie("_auth_type") || "Bearer";
  if (token) return `${type} ${token}`;
  return undefined;
}

// Keep in sync with ALLOWED_EVENT_TYPES in server/routes/track-routes.ts.
export type TrackEventType =
  | "page_view"
  | "area_toggle"
  | "cta_click"
  | "modal_open"
  | "forecast_range"
  | "feature_use";

/** Read utm_* params from the current URL. Returns undefined when none present. */
function readUtm(): { source?: string; medium?: string; campaign?: string } | undefined {
  try {
    const p = new URLSearchParams(window.location.search);
    const utm = {
      source: p.get("utm_source") || undefined,
      medium: p.get("utm_medium") || undefined,
      campaign: p.get("utm_campaign") || undefined,
    };
    return utm.source || utm.medium || utm.campaign ? utm : undefined;
  } catch {
    return undefined;
  }
}

export function track(eventType: TrackEventType, target?: string, metadata?: Record<string, unknown>) {
  const sessionId = getOrCreateSessionId();
  const authHeader = readAuthHeader();

  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (authHeader) headers["Authorization"] = authHeader;

  // Tag every event with its origin so the admin insights can separate the SPA
  // from the marketing site (both POST to the same /api/track + user_event table
  // and only store the pathname, which collides on shared paths like "/").
  const enrichedMetadata = { ...(metadata || {}), source: "app" };

  // Acquisition data is only meaningful on the landing page_view; the server
  // ignores it for in-app navigation (same-origin referrer → "direct").
  const acquisition =
    eventType === "page_view"
      ? { referrer: document.referrer || undefined, utm: readUtm() }
      : {};

  fetch(`${apiUrl}/api/track`, {
    method: "POST",
    headers,
    body: JSON.stringify({ eventType, target, sessionId, metadata: enrichedMetadata, ...acquisition }),
    keepalive: true,
  }).catch(() => {});
}
